/**
 * The map style chooser (D209) — the player's pick among the map styles the
 * pack offers.
 *
 * @remarks
 * Mike's two asks of 2026-09-25: the game builder selects which styles are
 * available, and either allows the choice or locks it to one. The pack's
 * `presentation.map.styles` is that selection; this control is the choice.
 * It renders ONLY when the pack offers more than one style — the shell's
 * rule for the settings popover (D204: nothing appears for a capability the
 * game does not have) — so a locked pack never shows a chooser of one.
 *
 * One native radio group, because a single choice among a few is exactly
 * what radios are: the arrow keys move the choice, the label is the hit
 * target, and assistive tech announces "Map, Plan, 1 of 2" with nothing
 * re-implemented. The inputs are visually hidden and the labels wear the
 * control row's family look, so the chooser sits beside the Images and
 * Sound switches as a sibling rather than a form.
 *
 * The words are the chrome's: `Plan` for the flat grid, `3D` (D210's
 * `scene`; Mike's word, 2026-09-27) for the space the player turns by
 * hand. Not `Scene`: the room's picture above the transcript is the thing
 * a player already calls the scene, and one word for two things on one
 * screen is a question nobody should have to ask. The hologram has no word
 * here (D215, Mike): it is never a button, only what `3D` shows where the
 * browser cannot draw it. The legend is `View`. A third style adds one key
 * to {@link MAP_STYLE_LABELS} and the type refuses to compile until it does.
 *
 * @packageDocumentation
 */

import { type MapView } from '@orpheus/api-contracts';

/** A map style as the wire names it — the pack's `presentation.map.style` vocabulary, read through the view rather than the schema so this package stays a renderer of views. */
type MapStyle = MapView['style'];

/** What each style is called on the control — the chrome's register, one word each. */
export const MAP_STYLE_LABELS: Readonly<Record<MapStyle, string>> = {
	basic: 'Plan',
	scene: '3D',
};

/** Props for {@link MapStyleChooserComponent}. */
export interface MapStyleChooserComponentProps {
	/** The style the player is looking at — always one of `offered`. */
	readonly chosen: MapStyle;
	/** Called with the style the player asked for. */
	readonly onChoose: (style: MapStyle) => void;
	/** The styles the pack offers, in the order it listed them. */
	readonly offered: readonly MapStyle[];
}

/**
 * Render the chooser.
 *
 * @param props - {@link MapStyleChooserComponentProps}
 * @returns The radio group
 */
export const MapStyleChooserComponent = ({ chosen, offered, onChoose }: MapStyleChooserComponentProps) => (
	<fieldset className="map-style-chooser">
		<legend className="map-style-chooser__legend">
			<MapStyleIcon />
			{/* D211: the chooser sits under the map's own heading now, so its word is the choice, not the map. Round 3 (Mike): `View`. */}
			View
		</legend>
		<div className="map-style-chooser__options">
			{offered.map((style) => (
				<label className="map-style-chooser__option" key={style}>
					<input
						checked={style === chosen}
						className="map-style-chooser__input"
						name="map-style"
						onChange={() => {
							onChoose(style);
						}}
						type="radio"
						value={style}
					/>
					<span className="map-style-chooser__label">{MAP_STYLE_LABELS[style]}</span>
				</label>
			))}
		</div>
	</fieldset>
);

/**
 * The legend's icon: two stacked plates — the map in the family's line
 * weight, distinct from the dialog trigger's own map icon.
 *
 * @returns The icon
 */
const MapStyleIcon = () => (
	<svg
		aria-hidden="true"
		className="map-style-chooser__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<path d="m12 4 8 4-8 4-8-4Z" />
		<path d="m4 12 8 4 8-4" />
		<path d="m4 16 8 4 8-4" />
	</svg>
);
